import { Button } from '@hg/stylizator/components'
import { useSizes } from '@hg/stylizator/hooks'
import { Text } from '@hg/stylizator/typography'
import { desktop } from '@hg/stylizator/utils'
import * as React from 'react'
import styled, { useTheme } from 'styled-components'

import SearchInput from 'app/components/blog/SearchInput'
import ClientOnly from 'app/components/ClientOnly'
import Pill from 'app/components/Pill'
import { isSSR } from 'app/helpers/utils'
import { useTranslation } from 'app/hooks/use-translation'
import SearchIcon from 'app/icons/Search'
import { Category } from 'app/service/api/theme/category'

interface IProps {
  categories: Category[]
  currentCategory?: Partial<Category>
  query?: string
}

const SearchAndCategories: React.FC<IProps> = ({ currentCategory, query, ...props }) => {
  const theme = useTheme()
  const t = useTranslation()
  const { isDesktop } = useSizes()
  const [isSearchOpen, setIsSearchOpen] = React.useState(false)
  const [search, setSearch] = React.useState(query ?? '')
  const searchRef = React.useRef<HTMLInputElement>(null)

  const origin = globalThis?.window?.location?.origin ?? ''
  const categories: Category[] = [{ name: t('all'), url: `${origin}/blog` }, ...props.categories]

  const handleSearch: React.FormEventHandler<HTMLFormElement | HTMLInputElement> = (event) => {
    event.preventDefault()

    if (search === query) {
      return
    }

    if (search) {
      const url = new URL(window.location.toString())
      url.pathname = '/search'
      url.searchParams.set('q', search)
      window.location.href = url.toString()
    }
  }

  const handleOpen = () => {
    if (isDesktop) {
      return
    }

    setIsSearchOpen(true)
    setTimeout(() => {
      searchRef.current?.focus()
    }, 10)
  }

  const handleClose = () => {
    setIsSearchOpen(false)
  }

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    setSearch(event.target.value)
  }

  return (
    <Container isSearchOpen={isSearchOpen}>
      <Title>
        <Text type="heading_medium" useCompact>
          <p>{t('discover_our_blog')}</p>
        </Text>
      </Title>

      <SearchBar id="search-bar">
        <ClientOnly>
          <Form id="form" onSubmit={handleSearch}>
            <SearchInput
              id="search"
              name="search"
              placeholder={t('search')}
              withIconRight={!isSSR && !isDesktop}
              autoFocus={!isSSR && !isDesktop}
              value={search}
              ref={searchRef}
              onClose={handleClose}
              onBlur={handleClose}
              onChange={handleChange}
              onSubmit={handleSearch}
              style={{ display: isDesktop || isSearchOpen ? 'block' : 'none' }}
            />
          </Form>
          {!isDesktop && !isSearchOpen ? (
            <Button type="secondary" size="small" style={{ width: 42, padding: 0 }} onClick={handleOpen}>
              <SearchIcon color={theme.color.blue[60]} width={26} height={26} />
            </Button>
          ) : null}
        </ClientOnly>
      </SearchBar>

      <Pills>
        <ClientOnly>
          {categories.map((category) => (
            <Pill key={category.url} href={category.url} isActive={category.name === currentCategory?.name}>
              {category.name}
            </Pill>
          ))}
        </ClientOnly>
      </Pills>
    </Container>
  )
}

const Container = styled.div<{ isSearchOpen?: boolean }>`
  position: relative;
  display: grid;
  gap: 24px 0;
  grid-template-columns: 1fr min-content;
  grid-template-rows: minmax(50px, auto) 1fr;
  grid-template-areas:
    'title search'
    'pills pills';

  @media ${desktop} {
    gap: 56px 64px;
    grid-template-columns: 60% 1fr;
    grid-template-rows: auto auto;
    grid-template-areas:
      'title title'
      'pills search';
  }
`

const Title = styled.div`
  grid-area: title;
  align-self: center;
`

const Pills = styled.div`
  grid-area: pills;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 16px;
`

const SearchBar = styled.div`
  grid-area: search;
  align-self: center;
`

const Form = styled.form`
  margin: 0;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;

  @media ${desktop} {
    position: relative;
  }
`

export default SearchAndCategories
